/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: accessControlApi.ts
 🗓️ Created: 2025-12-06
 🧩 Description:
    API client for Access Control module
────────────────────────────────────────────────────────────────*/

import { buildApiUrl, getApiHeaders } from '../../../config/api';
import type {
  AccessControlEntry,
  AccessControlFormData,
  AccessControlListResponse,
  AccessControlResponse,
} from '../types';

/**
 * Fetch access control entries list
 */
export async function fetchAccessControlEntries(params: {
  page: number;
  limit: number;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  search?: string;
  listType?: 'whitelist' | 'blacklist';
  direction?: 'input' | 'output' | 'forward';
  enabled?: boolean;
}): Promise<AccessControlListResponse> {
  const queryParams = new URLSearchParams({
    page: params.page.toString(),
    limit: params.limit.toString(),
    sortBy: params.sortBy,
    sortOrder: params.sortOrder,
  });

  if (params.search) {
    queryParams.append('search', params.search);
  }

  if (params.listType) {
    queryParams.append('listType', params.listType);
  }

  if (params.direction) {
    queryParams.append('direction', params.direction);
  }

  if (params.enabled !== undefined) {
    queryParams.append('enabled', params.enabled.toString());
  }

  const response = await fetch(
    buildApiUrl(`/api/admin/firewall/access-control?${queryParams}`),
    {
      headers: await getApiHeaders(),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch access control entries');
  }

  return response.json();
}

/**
 * Fetch access control entry by ID
 */
export async function fetchAccessControlEntryById(id: number): Promise<AccessControlEntry> {
  const response = await fetch(
    buildApiUrl(`/api/admin/firewall/access-control/${id}`),
    {
      headers: await getApiHeaders(),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch access control entry');
  }

  const result = await response.json();
  return result.data;
}

/**
 * Create access control entry
 */
export async function createAccessControlEntry(
  data: AccessControlFormData
): Promise<AccessControlEntry> {
  const response = await fetch(
    buildApiUrl('/api/admin/firewall/access-control'),
    {
      method: 'POST',
      headers: await getApiHeaders(),
      body: JSON.stringify(data),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to create access control entry');
  }

  const result = await response.json();
  return result.data;
}

/**
 * Update access control entry
 */
export async function updateAccessControlEntry(
  id: number,
  data: Partial<AccessControlFormData>
): Promise<AccessControlEntry> {
  const response = await fetch(
    buildApiUrl(`/api/admin/firewall/access-control/${id}`),
    {
      method: 'PUT',
      headers: await getApiHeaders(),
      body: JSON.stringify(data),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update access control entry');
  }

  const result = await response.json();
  return result.data;
}

/**
 * Delete access control entry
 */
export async function deleteAccessControlEntry(id: number): Promise<void> {
  const response = await fetch(
    buildApiUrl(`/api/admin/firewall/access-control/${id}`),
    {
      method: 'DELETE',
      headers: await getApiHeaders(),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to delete access control entry');
  }
}

/**
 * Apply Access Control rules to the system
 * This executes the nftables generator script on the server
 */
export async function applyAccessControlRules(): Promise<{
  success: boolean;
  message: string;
  output?: string;
  warnings?: string | null;
}> {
  const response = await fetch(
    buildApiUrl('/api/admin/firewall/access-control/apply'),
    {
      method: 'POST',
      headers: await getApiHeaders(),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    const details = error.details || error.stderr || '';
    throw new Error(`${error.error || 'Failed to apply access control rules'}${details ? ': ' + details : ''}`);
  }

  return response.json();
}

/**
 * Sync Fail2Ban banned IPs to database
 */
export async function syncFail2BanIPs(): Promise<{
  success: boolean;
  message: string;
  data: {
    total: number;
    added: number;
    jails: string[];
    ips: Array<{ ip: string; jail: string }>;
  };
}> {
  const response = await fetch(
    buildApiUrl('/api/admin/firewall/access-control/fail2ban/sync'),
    {
      headers: await getApiHeaders(),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to sync Fail2Ban banned IPs');
  }

  return response.json();
}

/**
 * Unban IP from Fail2Ban
 */
export async function unbanFail2BanIP(ip: string, jail?: string): Promise<{
  success: boolean;
  message: string;
  data: {
    ip: string;
    unbanned: boolean;
    removedFromDb: boolean;
  };
}> {
  const response = await fetch(
    buildApiUrl('/api/admin/firewall/access-control/fail2ban/unban'),
    {
      method: 'POST',
      headers: await getApiHeaders(),
      body: JSON.stringify({ ip, jail, deleteFromDb: true }),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to unban IP');
  }

  return response.json();
}

/**
 * Clear expired Access Control / Fail2Ban entries
 */
export async function clearExpiredAccessControlEntries(): Promise<{
  success: boolean;
  message: string;
  count: number;
}> {
  const response = await fetch(
    buildApiUrl('/api/admin/firewall/access-control/expired'),
    {
      method: 'DELETE',
      headers: await getApiHeaders(),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to clear expired entries');
  }

  return response.json();
}
